import { Grid, CardHeader, CardContent, Box, Select, FormHelperText, MenuItem, TextField, CircularProgress, Avatar, Button } from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik, FieldArray } from "formik";
import { IMAGE_BASE, REMOVEHTML, REMOVENBSP } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import { CategorySchema, PlaceBidTaskSchema } from "../../utils/Schema";
import { PlaceBidsServices } from "../../services/AllServices";
import EdiorJodit from "../../components/editor/editorJodit";
import { trimFormValues } from "../../utils/helpers";

function AddEditCategory() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [cmsData, setCmsData] = useState("");
    const fileInputRef = useRef(null);
    const [data, setData] = useState();
    const fetchTaskData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await PlaceBidsServices.getData();
            const resData = response?.data?.data;
            setImagePreview(IMAGE_BASE + resData?.thumbnail);
            setData(resData);
            setCmsData(resData?.notice)
            setLoading(false);
        } catch (error) {
            errorHandler(error);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTaskData();
    }, [fetchTaskData]);

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: PlaceBidTaskSchema,
        initialValues: {
            title: data?.title || "",
            shareBids: data?.shareBids || "",
            freeBidsGranted: data?.freeBidsGranted || "",
            notice: data?.notice || "",
            image: data?.thumbnail || "",
            status: data?.status ? true : false,
        },
        onSubmit: async (values) => {
            setLoading(true);
            try {
                // Trim all string values in the form values
                const trimmedValues = trimFormValues(values);
                
                const formData = new FormData();
                formData.append("title", trimmedValues.title);
                formData.append("shareBids", trimmedValues.shareBids);
                formData.append("freeBidsGranted", trimmedValues.freeBidsGranted);
                formData.append("notice", trimmedValues.notice);
                formData.append("status", trimmedValues.status);
                formData.append("imagePath", "task");
                if (selectedImage && selectedImage !== data?.image) {
                    formData.append("image", selectedImage);
                }
                const response = await PlaceBidsServices.update(formData);

                toast.success(response?.data?.message, { toastId: "success" });
                // navigate(-1);
            } catch (error) {
                console.error(error);
                errorHandler(error);
            } finally {
                setLoading(false);
            }
        },
    });

    const handleImageChange = (event) => {
        const file = event.currentTarget.files[0];
        const validTypes = ["image/jpeg", "image/png", "image/gif"];

        if (selectedImage && selectedImage.name === file?.name) {
            toast.error("You cannot upload the same image again.");
            fileInputRef.current.value = "";
            return;
        }

        if (file && validTypes.includes(file.type)) {
            setImageLoading(true);
            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
            formik.setFieldValue("image", file);
            setTimeout(() => {
                setImageLoading(false);
            }, 2000);
        } else {
            toast.error("Please upload a valid image (JPEG, PNG, or GIF).");
            fileInputRef.current.value = "";
        }
    };

    return (
        <Box>
            <CardLayout>
                <CardHeader title={`Place Bids in a Day & Earn`} />
                <CardContent>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }} md={12}>
                                    <input
                                        accept="image/*"
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageChange}
                                        style={{ display: "none" }}
                                    />

                                    <Box
                                        mt={2}
                                        onClick={() => fileInputRef.current.click()}
                                        className="upload-box"
                                    >
                                        {imageLoading ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            <Avatar
                                                src={imagePreview || "/path/to/default-avatar.jpg"}
                                                alt="preview"
                                                className="avatar"
                                            />
                                        )}
                                    </Box>
                                    {formik.touched.image && formik.errors.image ? (
                                        <div className="error-msg error-category">
                                            {formik.errors.image}
                                        </div>
                                    ) : null}

                                    <Box mt={2} display="flex" justifyContent="center">
                                        <Button
                                            size="large"
                                            type="button"
                                            variant="contained"
                                            onClick={() => fileInputRef.current.click()}
                                        >
                                            Upload Image
                                        </Button>
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 6 }} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        name="title"
                                        error={Boolean(formik.touched.title && formik.errors.title)}
                                        helperText={formik.touched.title && formik.errors.title}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                </Grid>

                                <Grid size={{ xs: 6 }} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Place Bids Quantity"
                                        value={formik.values.shareBids}
                                        onChange={(e) => {
                                            const onlyInts = e.target.value;
                                            if (/^\d*$/.test(onlyInts)) {
                                                formik.setFieldValue("shareBids", onlyInts);
                                            }
                                        }}
                                        name="shareBids"
                                        error={Boolean(formik.touched.shareBids && formik.errors.shareBids)}
                                        helperText={formik.touched.shareBids && formik.errors.shareBids}
                                        inputProps={{ maxLength: 4, inputMode: "numeric" }}
                                    />

                                </Grid>

                                <Grid size={{ xs: 12 }} md={12}>
                                    <TextField
                                        fullWidth
                                        label="Free Bids"
                                        value={formik.values.freeBidsGranted}
                                        onChange={formik.handleChange}
                                        name="freeBidsGranted"
                                        error={Boolean(formik.touched.freeBidsGranted && formik.errors.freeBidsGranted)}
                                        helperText={formik.touched.freeBidsGranted && formik.errors.freeBidsGranted}
                                        inputProps={{ maxLength: 50 }}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12 }} md={12}>
                                    <EdiorJodit
                                        data={formik?.values?.notice}
                                        placeholder="Terms and conditions"
                                        onChange={(html) => {
                                            formik.setFieldTouched("notice", true);
                                            const withoutHtml = html.replace(REMOVEHTML, "").replace(REMOVENBSP, "").trim();
                                            formik.setFieldValue("notice", withoutHtml ? html : "");
                                            setCmsData(html);
                                        }}
                                    />

                                    {formik.touched.notice && formik.errors.notice && (
                                        <p style={{ color: "red", fontSize: "14px" }}>
                                            {formik.errors.notice}
                                        </p>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12 }} md={12}>
                                    <Select
                                        labelId="status-label"
                                        id="status"
                                        value={formik.values.status}
                                        onChange={(e) =>
                                            formik.setFieldValue("status", e.target.value)
                                        }
                                        fullWidth
                                        error={Boolean(
                                            formik.touched.status && formik.errors.status
                                        )}
                                        displayEmpty
                                        defaultValue=""
                                    >
                                        <MenuItem value="">Select Status</MenuItem>
                                        <MenuItem value='true'>Active</MenuItem>
                                        <MenuItem value='false'>Inactive</MenuItem>
                                    </Select>
                                    {formik.touched.status && formik.errors.status && (
                                        <FormHelperText error>
                                            {formik.errors.status}
                                        </FormHelperText>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Box mt={3} width="100%">
                                        <LoadingButton
                                            fullWidth
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                            loading={loading}
                                        >
                                            Update Auction
                                        </LoadingButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Form>
                    </FormikProvider>
                </CardContent>
            </CardLayout>
        </Box>
    );
}

export default AddEditCategory;
